import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';

import { buildZodSchema } from './utils/json-schema-to-zod';
import { buildFieldConfigs } from './utils/json-schema-to-fields';
import { answersToFormValues } from './utils/answers-transform';
import { parseJsonSafe } from './utils/parse-json-safe';
import { ConditionalField } from './ConditionalField';
import { DynamicField } from './DynamicField';
import type {
  DynamicCustomFormProps,
  JsonSchema,
  UiSchema,
  FieldConfig,
} from './types';

// Re-export types and utilities for consumers
export type {
  DynamicCustomFormProps,
  CustomFormData,
  CustomFormResponseData,
  JsonSchema,
  UiSchema,
  FieldConfig,
} from './types';
export {
  answersToFormValues,
  formValuesToAnswers,
} from './utils/answers-transform';
export { buildZodSchema } from './utils/json-schema-to-zod';
export { buildFieldConfigs } from './utils/json-schema-to-fields';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildDefaultValues(fields: FieldConfig[]): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};
  for (const field of fields) {
    switch (field.type) {
      case 'checkbox':
        defaults[field.name] = false;
        break;
      case 'number':
        defaults[field.name] = '';
        break;
      default:
        defaults[field.name] = '';
    }
  }
  return defaults;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function DynamicCustomForm({
  customForm,
  customFormResponse,
  onSubmit,
  onCancel,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  isLoading = false,
  className,
}: DynamicCustomFormProps) {
  const formSchema = useMemo(
    () => parseJsonSafe<JsonSchema>(customForm.formSchemaJson),
    [customForm.formSchemaJson],
  );
  const uiSchema = useMemo(
    () => parseJsonSafe<UiSchema>(customForm.uiSchemaJson),
    [customForm.uiSchemaJson],
  );

  const zodSchema = useMemo(() => buildZodSchema(formSchema), [formSchema]);
  const fieldConfigs = useMemo(
    () => buildFieldConfigs(formSchema, uiSchema),
    [formSchema, uiSchema],
  );

  const defaultValues = useMemo(() => {
    if (customFormResponse?.answers?.answersJson) {
      return {
        ...buildDefaultValues(fieldConfigs),
        ...answersToFormValues(customFormResponse.answers.answersJson),
      };
    }
    return buildDefaultValues(fieldConfigs);
  }, [customFormResponse, fieldConfigs]);

  const form = useForm<Record<string, unknown>>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(zodSchema) as any,
    defaultValues,
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    await onSubmit(values as Record<string, unknown>);
  });

  if (
    !formSchema.properties ||
    Object.keys(formSchema.properties).length === 0
  ) {
    return <p className="text-muted-foreground">This form has no fields.</p>;
  }

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className={className}>
        <div className="space-y-4">
          {fieldConfigs.map((config) => (
            <ConditionalField
              key={config.name}
              dependsOn={config.dependsOn}
              control={form.control}
            >
              <FormField
                control={form.control}
                name={config.name}
                render={({ field }) => (
                  <FormItem>
                    {/* For checkbox the label is rendered inside DynamicField */}
                    {config.type !== 'checkbox' && (
                      <FormLabel>
                        {config.label}
                        {config.required && (
                          <span className="text-destructive ml-1">*</span>
                        )}
                      </FormLabel>
                    )}
                    <FormControl>
                      <DynamicField config={config} field={field} />
                    </FormControl>
                    {config.description && (
                      <FormDescription>{config.description}</FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </ConditionalField>
          ))}
        </div>

        <div className="flex items-center gap-3 pt-6">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Submitting\u2026' : submitLabel}
          </Button>
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              {cancelLabel}
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}

export default DynamicCustomForm;
